import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Grid,
  Chip,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { androidColors } from '../../theme/androidTheme';
import Layout from '../../components/Layout/Layout';
import PageTitle from '../../components/PageTitle/PageTitle';
import SimulatorChart from '../../components/Charts/SimulatorChart';
import { cryptoService } from '../../services/cryptoService';

interface CryptoData {
  symbol: string;
  amount: number;
  currentPrice: number;
}

interface HistoricalData {
  date: string;
  price: number;
  volume: number;
  high: number;
  low: number;
}

interface ProjectionData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    borderColor: string;
    backgroundColor: string[];
    fill: boolean;
  }[];
}

interface CryptoAnalysis {
  symbol: string;
  monthlyReturns: number[];
  averageMonthlyReturn: number;
  volatility: number;
  sharpeRatio: number;
  maxDrawdown: number;
  trendStrength: number;
  recentMomentum: number;
  predictedAnnualReturn: number;
  confidence: number;
}

const WealthSimulator: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [projectionData, setProjectionData] = useState<ProjectionData>({
    labels: [],
    datasets: [{
      label: t('wealth.currentValue'),
      data: [],
      borderColor: androidColors.primaryGold,
      backgroundColor: [`${androidColors.primaryGold}60`],
      fill: true,
    }],
  });
  const [currentPortfolioValue, setCurrentPortfolioValue] = useState<number>(0);
  const [projectedValue10Years, setProjectedValue10Years] = useState<number>(0);
  const [portfolioAnnualReturn, setPortfolioAnnualReturn] = useState<number>(0);
  const [portfolioVolatility, setPortfolioVolatility] = useState<number>(0);
  const [cryptoAnalyses, setCryptoAnalyses] = useState<CryptoAnalysis[]>([]);

  // Fonction pour calculer la moyenne géométrique des rendements
  const calculateGeometricMean = (returns: number[]): number => {
    if (returns.length === 0) return 0;
    const product = returns.reduce((acc, ret) => acc * (1 + ret), 1);
    return Math.pow(product, 1 / returns.length) - 1;
  };

  // Fonction pour calculer la volatilité (écart-type annualisé)
  const calculateVolatility = (returns: number[]): number => {
    if (returns.length <= 1) return 0;
    const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((acc, ret) => acc + Math.pow(ret - mean, 2), 0) / (returns.length - 1);
    return Math.sqrt(variance) * Math.sqrt(12); // Annualisé
  };

  // Fonction pour calculer le ratio de Sharpe (rendement ajusté au risque)
  const calculateSharpeRatio = (returns: number[], riskFreeRate: number = 0.02): number => {
    const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const volatility = calculateVolatility(returns) / Math.sqrt(12); // Mensuel
    const annualReturn = Math.pow(1 + avgReturn, 12) - 1;
    return volatility === 0 ? 0 : (annualReturn - riskFreeRate) / (volatility * Math.sqrt(12));
  };

  // Fonction pour calculer le drawdown maximum
  const calculateMaxDrawdown = (prices: number[]): number => {
    if (prices.length < 2) return 0;
    let maxDrawdown = 0;
    let peak = prices[0];
    
    for (let i = 1; i < prices.length; i++) {
      if (prices[i] > peak) {
        peak = prices[i];
      } else {
        const drawdown = (peak - prices[i]) / peak;
        maxDrawdown = Math.max(maxDrawdown, drawdown);
      }
    }
    return maxDrawdown;
  };

  // Fonction pour calculer la force de tendance (régression linéaire)
  const calculateTrendStrength = (returns: number[]): number => {
    if (returns.length < 3) return 0;
    const n = returns.length;
    const x = Array.from({length: n}, (_, i) => i);
    const y = returns;
    
    const sumX = x.reduce((a, b) => a + b, 0);
    const sumY = y.reduce((a, b) => a + b, 0);
    const sumXY = x.reduce((a, b, i) => a + b * y[i], 0);
    const sumXX = x.reduce((a, b) => a + b * b, 0);
    
    const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
    
    // Coefficient de corrélation pour mesurer la force de la tendance
    const meanX = sumX / n;
    const meanY = sumY / n;
    const numerator = x.reduce((acc, xi, i) => acc + (xi - meanX) * (y[i] - meanY), 0);
    const denomX = Math.sqrt(x.reduce((acc, xi) => acc + Math.pow(xi - meanX, 2), 0));
    const denomY = Math.sqrt(y.reduce((acc, yi) => acc + Math.pow(yi - meanY, 2), 0));
    
    const correlation = denomX === 0 || denomY === 0 ? 0 : numerator / (denomX * denomY);
    
    return slope * Math.abs(correlation); // Tendance pondérée par la force de corrélation
  };

  // Modèle de prédiction amélioré
  const predictFutureReturn = (analysis: CryptoAnalysis): number => {
    const {
      averageMonthlyReturn,
      volatility,
      sharpeRatio,
      maxDrawdown,
      trendStrength,
      recentMomentum
    } = analysis;

    // Convertir le rendement mensuel en annuel
    const historicalAnnualReturn = Math.pow(1 + averageMonthlyReturn, 12) - 1;
    
    // Facteurs d'ajustement
    const volatilityPenalty = Math.max(0, volatility - 0.5) * 0.1; // Pénalité si volatilité > 50%
    const drawdownPenalty = maxDrawdown * 0.15; // Pénalité basée sur le drawdown max
    const sharpeBonus = Math.max(0, sharpeRatio - 0.5) * 0.05; // Bonus si Sharpe > 0.5
    const trendAdjustment = trendStrength * 0.3; // Ajustement selon la tendance
    const momentumAdjustment = recentMomentum * 0.2; // Ajustement selon le momentum récent
    
    // Régression vers la moyenne du marché crypto (approximativement 15% annuel à long terme)
    const marketMean = 0.15;
    const meanReversionFactor = 0.3; // 30% de régression vers la moyenne
    
    let predictedReturn = historicalAnnualReturn;
    predictedReturn += trendAdjustment + momentumAdjustment + sharpeBonus;
    predictedReturn -= volatilityPenalty + drawdownPenalty;
    
    // Appliquer la régression vers la moyenne
    predictedReturn = predictedReturn + meanReversionFactor * (marketMean - predictedReturn);
    
    // Limiter les prédictions à des valeurs réalistes
    return Math.max(-0.5, Math.min(1.5, predictedReturn)); // Entre -50% et +150%
  };

  // Fonction pour calculer la confiance dans la prédiction
  const calculatePredictionConfidence = (analysis: CryptoAnalysis): number => {
    const dataQuality = Math.min(1, analysis.monthlyReturns.length / 12); // Plus de données = plus de confiance
    const stabilityScore = 1 / (1 + analysis.volatility); // Moins de volatilité = plus de confiance
    const trendConsistency = Math.abs(analysis.trendStrength) > 0.01 ? 0.8 : 0.5; // Tendance claire = plus de confiance
    
    return (dataQuality * 0.4 + stabilityScore * 0.4 + trendConsistency * 0.2);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // 1. Récupérer les données du portefeuille
        const portfolioData = await cryptoService.getPortfolioData();
        
        if (!portfolioData || !portfolioData.cryptos || portfolioData.cryptos.length === 0) {
          throw new Error(t('wealth.noPortfolio'));
        }

        const cryptos: CryptoData[] = portfolioData.cryptos.map(crypto => ({
          symbol: crypto.symbol,
          amount: crypto.amount,
          currentPrice: crypto.price_current,
        }));

        // 2. Calculer la valeur actuelle du portefeuille
        const currentValue = cryptos.reduce((acc, crypto) => {
          return acc + (crypto.amount * crypto.currentPrice);
        }, 0);
        setCurrentPortfolioValue(currentValue);

        // 3. Calculer les poids de chaque crypto dans le portefeuille
        const portfolioWeights: { [symbol: string]: number } = {};
        cryptos.forEach(crypto => {
          portfolioWeights[crypto.symbol] = (crypto.amount * crypto.currentPrice) / currentValue;
        });

        // 4. Récupérer et analyser les données historiques
        const analyses: CryptoAnalysis[] = [];
        const cryptosWithoutData: string[] = [];

        for (const crypto of cryptos) {
          try {
            const candlesResponse = await fetch(
              `https://candles-cryptonvx.candles-api.workers.dev/v1/candles?symbol=${crypto.symbol}&period=1Y`
            );
            
            if (candlesResponse.ok) {
              const candlesData = await candlesResponse.json();
              if (candlesData.candles && candlesData.candles.length > 30) { // Au moins 30 points
                const sortedCandles = candlesData.candles.sort((a: any, b: any) => a.timestamp - b.timestamp);
                
                // Calculer les rendements mensuels
                const monthlyReturns: number[] = [];
                const prices: number[] = [];
                
                // Grouper par mois et calculer les rendements
                let currentMonth = -1;
                let monthStartPrice = 0;
                
                sortedCandles.forEach((candle: any) => {
                  const date = new Date(candle.timestamp * 1000);
                  const month = date.getMonth() + date.getFullYear() * 12;
                  const price = parseFloat(candle.close);
                  
                  prices.push(price);
                  
                  if (currentMonth === -1) {
                    currentMonth = month;
                    monthStartPrice = price;
                  } else if (month !== currentMonth) {
                    // Nouveau mois, calculer le rendement du mois précédent
                    const monthlyReturn = (price - monthStartPrice) / monthStartPrice;
                    if (monthlyReturn > -0.95 && monthlyReturn < 10) { // Filtrer les valeurs aberrantes
                      monthlyReturns.push(monthlyReturn);
                    }
                    currentMonth = month;
                    monthStartPrice = price;
                  }
                });

                if (monthlyReturns.length >= 3) {
                  const averageMonthlyReturn = calculateGeometricMean(monthlyReturns);
                  const volatility = calculateVolatility(monthlyReturns);
                  const sharpeRatio = calculateSharpeRatio(monthlyReturns);
                  const maxDrawdown = calculateMaxDrawdown(prices);
                  const trendStrength = calculateTrendStrength(monthlyReturns);
                  const recentMomentum = monthlyReturns.slice(-3).reduce((a, b) => a + b, 0) / 3;

                  const analysis: CryptoAnalysis = {
                    symbol: crypto.symbol,
                    monthlyReturns,
                    averageMonthlyReturn,
                    volatility,
                    sharpeRatio,
                    maxDrawdown,
                    trendStrength,
                    recentMomentum,
                    predictedAnnualReturn: 0, // Sera calculé
                    confidence: 0 // Sera calculé
                  };

                  analysis.predictedAnnualReturn = predictFutureReturn(analysis);
                  analysis.confidence = calculatePredictionConfidence(analysis);
                  
                  analyses.push(analysis);
                } else {
                  cryptosWithoutData.push(crypto.symbol);
                }
              } else {
                cryptosWithoutData.push(crypto.symbol);
              }
            } else {
              cryptosWithoutData.push(crypto.symbol);
            }
          } catch (err) {
            console.warn(`Erreur pour ${crypto.symbol}:`, err);
            cryptosWithoutData.push(crypto.symbol);
          }
        }

        setCryptoAnalyses(analyses);

        // 5. Calculer les métriques du portefeuille
        let portfolioExpectedReturn = 0;
        let portfolioVariance = 0;
        let totalAnalyzedWeight = 0;

        analyses.forEach(analysis => {
          const weight = portfolioWeights[analysis.symbol] || 0;
          if (weight > 0) {
            portfolioExpectedReturn += analysis.predictedAnnualReturn * weight * analysis.confidence;
            portfolioVariance += Math.pow(analysis.volatility * weight, 2);
            totalAnalyzedWeight += weight * analysis.confidence;
          }
        });

        // Normaliser si on n'a pas analysé tout le portefeuille
        if (totalAnalyzedWeight > 0 && totalAnalyzedWeight < 1) {
          portfolioExpectedReturn /= totalAnalyzedWeight;
        }

        const portfolioVol = Math.sqrt(portfolioVariance);
        
        setPortfolioAnnualReturn(portfolioExpectedReturn * 100);
        setPortfolioVolatility(portfolioVol * 100);

        // 6. Créer les projections avec simulation Monte Carlo simplifiée
        const projections: ProjectionData = {
          labels: [],
          datasets: [{
            label: t('wealth.currentValue'),
            data: [],
            borderColor: androidColors.primaryGold,
            backgroundColor: [`${androidColors.primaryGold}60`],
            fill: true,
          }],
        };
        
        const currentYear = new Date().getFullYear();
        projections.labels.push(currentYear.toString());
        projections.datasets[0].data.push(currentValue);

        let projectedValue = currentValue;
        
        // Projection sur 10 ans avec facteur de décroissance
        for (let year = 1; year <= 10; year++) {
          // Facteur de décroissance : les rendements diminuent progressivement
          const maturityFactor = Math.pow(0.92, year - 1); // Décroissance de 8% par an
          
          // Ajustement aléatoire pour simuler la volatilité (simplifié)
          const volatilityAdjustment = (Math.random() - 0.5) * portfolioVol * 0.5;
          
          const yearlyReturn = portfolioExpectedReturn * maturityFactor + volatilityAdjustment;
          
          // Limiter les rendements annuels extrêmes
          const limitedReturn = Math.max(-0.6, Math.min(1.2, yearlyReturn));
          
          projectedValue *= (1 + limitedReturn);
          
          projections.labels.push((currentYear + year).toString());
          projections.datasets[0].data.push(Math.max(currentValue * 0.1, projectedValue)); // Plancher à 10% de la valeur initiale
        }

        setProjectedValue10Years(projectedValue);
        setProjectionData(projections);

        if (cryptosWithoutData.length > 0) {
          setError(t('wealth.insufficientData', { list: cryptosWithoutData.join(', '), count: analyses.length }));
        }

      } catch (err) {
        console.error('Erreur dans fetchData:', err);
        setError(err instanceof Error ? err.message : t('common.fetchError'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat(i18n.language === 'fr' ? 'fr-FR' : undefined, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
  };

  return (
    <Layout title={t('wealth.title')}>
      <PageTitle title={t('wealth.pageTitle')} />
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Typography
          variant="h5"
          sx={{
            color: androidColors.textColor,
            fontWeight: 'bold',
            mb: 3,
          }}
        >
          {t('wealth.heading')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress sx={{ color: androidColors.primaryGold }} />
            <Typography sx={{ ml: 2, color: androidColors.textColor }}>
              {t('wealth.loading')}
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="warning" sx={{ mb: 3 }}>
            {error}
          </Alert>
        ) : (
          <>
            {/* Statistiques du portefeuille */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }}>
              <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: androidColors.textSecondary }}>
                    {t('wealth.currentValue')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: androidColors.primaryGold, fontWeight: 'bold' }}>
                    {formatCurrency(currentPortfolioValue)}
                  </Typography>
                </CardContent>
              </Card>
              
              <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: androidColors.textSecondary }}>
                    {t('wealth.expectedReturn')}
                  </Typography>
                  <Typography variant="h5" sx={{ 
                    color: portfolioAnnualReturn >= 0 ? androidColors.success : androidColors.error, 
                    fontWeight: 'bold' 
                  }}>
                    {formatPercentage(portfolioAnnualReturn)}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: androidColors.textSecondary }}>
                    {t('wealth.volatility')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
                    {formatPercentage(portfolioVolatility)}
                  </Typography>
                </CardContent>
              </Card>

              <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px' }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ color: androidColors.textSecondary }}>
                    {t('wealth.projection10y')}
                  </Typography>
                  <Typography variant="h5" sx={{ color: androidColors.success, fontWeight: 'bold' }}>
                    {formatCurrency(projectedValue10Years)}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {/* Graphique principal */}
            <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px', mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: androidColors.textColor, mb: 2 }}>
                  {t('wealth.projectedEvolution')}
                </Typography>
                <Box sx={{ height: 400 }}>
                  <SimulatorChart data={projectionData} />
                </Box>
              </CardContent>
            </Card>

            {/* Analyse détaillée par crypto */}
            {cryptoAnalyses.length > 0 && (
              <Card sx={{ backgroundColor: androidColors.cardBackground, border: `1px solid ${androidColors.borderColor}`, borderRadius: '12px', mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: androidColors.textColor, mb: 2 }}>
                    {t('wealth.detailedAnalysis')}
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 2 }}>
                    {cryptoAnalyses.map((analysis) => (
                      <Box key={analysis.symbol} sx={{ 
                        p: 2, 
                        backgroundColor: `${androidColors.primaryGold}10`, 
                        borderRadius: '8px' 
                      }}>
                        <Typography variant="subtitle1" sx={{ color: androidColors.textColor, fontWeight: 'bold' }}>
                          {analysis.symbol}
                        </Typography>
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1, mt: 1 }}>
                          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                            {t('wealth.expectedReturnShort')}: <span style={{ color: analysis.predictedAnnualReturn >= 0 ? androidColors.success : androidColors.error }}>
                              {formatPercentage(analysis.predictedAnnualReturn * 100)}
                            </span>
                          </Typography>
                          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                            {t('wealth.volatility')}: {formatPercentage(analysis.volatility * 100)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                            {t('wealth.sharpe')}: {analysis.sharpeRatio.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ color: androidColors.textSecondary }}>
                            {t('wealth.confidence')}: {(analysis.confidence * 100).toFixed(0)}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Méthodologie */}
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                <strong>{t('wealth.methodTitle')}</strong> {t('wealth.methodText')}
              </Typography>
            </Alert>

            <Typography variant="body2" sx={{ color: androidColors.textSecondary, textAlign: 'center', fontStyle: 'italic' }}>
              {t('wealth.footerNote')}
            </Typography>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>{t('market.warningTitle')}</strong> {t('market.warningText')}
              </Typography>
            </Alert>
          </>
        )}
      </Box>
    </Layout>
  );
};

export { WealthSimulator };
export default WealthSimulator;